/**
 * Sync package — the manual phone → PC transfer file (decision Q2, format v1).
 *
 * A package carries (1) the records that changed, for the other app instance to merge, and
 * (2) the Excel projection of every ledger change, for the Excel helper (a later step) to apply.
 *
 * Idempotency and forward compatibility:
 *  - Every record keeps its UUID and a monotonically increasing `version`. Import keeps the
 *    higher version; equal versions are skipped. Importing the same package twice changes nothing.
 *  - Packages are checksummed (sha-256 of the canonical payload) and carry `format`,
 *    `formatVersion` and `schemaVersion`. Readers accept equal or older versions and ignore
 *    unknown fields; they refuse newer major versions instead of guessing.
 *  - Nothing is deleted by an import: voids travel as records with status `void`.
 */
import { z } from "zod";
import { projectExpense, projectIncome, type ExcelIncomeOperation, type ExcelLedgerOperation } from "@/domain/excel-projection";
import type {
  BalanceAnchor,
  BudgetLine,
  Category,
  CategoryGroup,
  Expense,
  Income,
  IncomeBudget,
  PaymentMethod,
  SyncState,
} from "@/domain/model";
import {
  balanceAnchorIssues,
  budgetLineIssues,
  expenseIssues,
  incomeBudgetIssues,
  incomeIssues,
  type ReferenceContext,
} from "@/domain/validation";
import { upgradeExpenseRecord } from "@/domain/record-upgrades";
import { canonicalJson, newId, sha256Hex } from "@/lib/hash";
import { SCHEMA_VERSION, nowIso, type BudgetDatabase } from "./db";

export const SYNC_FORMAT = "bilancio-sync";
export const SYNC_FORMAT_VERSION = 1;

export interface SyncPayload {
  reference: { categoryGroups: CategoryGroup[]; categories: Category[]; paymentMethods: PaymentMethod[] };
  records: {
    expenses: Expense[];
    incomes: Income[];
    budgets: BudgetLine[];
    incomeBudgets: IncomeBudget[];
    balanceAnchors: BalanceAnchor[];
  };
  excel: { ledger: ExcelLedgerOperation[]; income: ExcelIncomeOperation[] };
}

export interface SyncPackage {
  format: typeof SYNC_FORMAT;
  formatVersion: number;
  schemaVersion: number;
  packageId: string;
  createdAt: string;
  source: { deviceId: string };
  mode: "changes" | "full";
  /** For `changes`: records updated after this instant were included. */
  since: string | null;
  counts: Record<string, number>;
  checksum: string;
  payload: SyncPayload;
}

export class SyncPackageError extends Error {
  override readonly name = "SyncPackageError";
  constructor(
    message: string,
    readonly problems: string[] = [],
  ) {
    super(message);
  }
}

const LAST_EXPORT_KEY = "sync.lastExportAt";

export async function getDeviceId(db: BudgetDatabase): Promise<string> {
  const row = await db.settings.get("device.id");
  if (row) return row.value as string;
  const id = newId();
  await db.settings.put({ key: "device.id", value: id });
  return id;
}

export async function getLastExportAt(db: BudgetDatabase): Promise<string | null> {
  return ((await db.settings.get(LAST_EXPORT_KEY))?.value as string | undefined) ?? null;
}

/** Records that would go in a "changes" package now. */
export async function countPendingChanges(db: BudgetDatabase): Promise<number> {
  const since = await getLastExportAt(db);
  const newer = <T extends { updatedAt: string }>(rows: T[]) => rows.filter((r) => !since || r.updatedAt > since).length;
  const [e, i, b, ib, a] = await Promise.all([
    db.expenses.toArray(),
    db.incomes.toArray(),
    db.budgets.toArray(),
    db.incomeBudgets.toArray(),
    db.balanceAnchors.toArray(),
  ]);
  // Excel-imported history is already in the workbook and does not need to travel back.
  return newer(e.filter((x) => x.source !== "excel_import")) + newer(i.filter((x) => x.source !== "excel_import")) + newer(b) + newer(ib) + newer(a);
}

export async function buildSyncPackage(db: BudgetDatabase, mode: "changes" | "full"): Promise<SyncPackage> {
  const since = mode === "changes" ? await getLastExportAt(db) : null;
  const changed = <T extends { updatedAt: string }>(rows: T[]) => (since ? rows.filter((r) => r.updatedAt > since) : rows);
  const [groups, categories, paymentMethods, expenses, incomes, budgets, incomeBudgets, anchors] = await Promise.all([
    db.categoryGroups.toArray(),
    db.categories.toArray(),
    db.paymentMethods.toArray(),
    db.expenses.toArray(),
    db.incomes.toArray(),
    db.budgets.toArray(),
    db.incomeBudgets.toArray(),
    db.balanceAnchors.toArray(),
  ]);
  const appExpenses = changed(expenses.filter((e) => e.source !== "excel_import" && e.status !== "draft"));
  const appIncomes = changed(incomes.filter((i) => i.source !== "excel_import"));
  const categoryLabel = new Map(categories.map((c) => [c.id, c.excelLabel]));
  const methodLabel = new Map(paymentMethods.map((p) => [p.id, p.excelLabel]));

  const payload: SyncPayload = {
    reference: { categoryGroups: groups, categories, paymentMethods },
    records: {
      expenses: appExpenses,
      incomes: appIncomes,
      budgets: changed(budgets),
      incomeBudgets: changed(incomeBudgets),
      balanceAnchors: changed(anchors),
    },
    excel: {
      ledger: appExpenses.map((e) => projectExpense(e, categoryLabel.get(e.categoryId) ?? "", e.paymentMethodId ? (methodLabel.get(e.paymentMethodId) ?? "") : "")),
      income: appIncomes.map(projectIncome),
    },
  };
  const createdAt = nowIso();
  const pkg: SyncPackage = {
    format: SYNC_FORMAT,
    formatVersion: SYNC_FORMAT_VERSION,
    schemaVersion: SCHEMA_VERSION,
    packageId: newId(),
    createdAt,
    source: { deviceId: await getDeviceId(db) },
    mode,
    since,
    counts: {
      expenses: payload.records.expenses.length,
      incomes: payload.records.incomes.length,
      budgets: payload.records.budgets.length,
      incomeBudgets: payload.records.incomeBudgets.length,
      balanceAnchors: payload.records.balanceAnchors.length,
      excelLedgerOperations: payload.excel.ledger.length,
    },
    checksum: await sha256Hex(canonicalJson(payload)),
    payload,
  };
  return pkg;
}

/** Call after the file has been handed to the user: marks included ledger records as exported. */
export async function markPackageExported(db: BudgetDatabase, pkg: SyncPackage): Promise<void> {
  await db.transaction("rw", [db.expenses, db.incomes, db.settings, db.transfers], async () => {
    for (const e of pkg.payload.records.expenses) {
      const local = await db.expenses.get(e.id);
      if (local && local.version === e.version && local.sync.status === "pending") {
        await db.expenses.update(e.id, { sync: { ...local.sync, status: "exported", exportedAt: pkg.createdAt } });
      }
    }
    for (const i of pkg.payload.records.incomes) {
      const local = await db.incomes.get(i.id);
      if (local && local.version === i.version && local.sync.status === "pending") {
        await db.incomes.update(i.id, { sync: { ...local.sync, status: "exported", exportedAt: pkg.createdAt } });
      }
    }
    await db.settings.put({ key: LAST_EXPORT_KEY, value: pkg.createdAt });
    await db.transfers.add({ id: newId(), kind: "sync-export", createdAt: nowIso(), summary: { packageId: pkg.packageId, mode: pkg.mode, counts: pkg.counts } });
  });
}

const packageShape = z.object({
  format: z.literal(SYNC_FORMAT),
  formatVersion: z.number().int(),
  schemaVersion: z.number().int(),
  packageId: z.string(),
  createdAt: z.string(),
  source: z.object({ deviceId: z.string() }),
  mode: z.enum(["changes", "full"]),
  since: z.string().nullable(),
  counts: z.record(z.string(), z.number()),
  checksum: z.string().length(64),
  payload: z.object({
    reference: z.object({ categoryGroups: z.array(z.any()), categories: z.array(z.any()), paymentMethods: z.array(z.any()) }),
    records: z.object({
      expenses: z.array(z.any()),
      incomes: z.array(z.any()),
      budgets: z.array(z.any()),
      incomeBudgets: z.array(z.any()),
      balanceAnchors: z.array(z.any()),
    }),
    excel: z.object({ ledger: z.array(z.any()), income: z.array(z.any()) }),
  }),
});

function guard(fn: () => string[]): string[] {
  try {
    return fn();
  } catch (error) {
    return [`record malformato (${(error as Error).message})`];
  }
}

/** Parses and validates a package (format, version, checksum, every record). No writes. */
export async function readSyncPackage(json: string): Promise<SyncPackage> {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new SyncPackageError("Il file non è un JSON valido.");
  }
  const parsed = packageShape.safeParse(raw);
  if (!parsed.success) {
    throw new SyncPackageError("Il file non è un pacchetto di sincronizzazione di Bilancio.", parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`));
  }
  const pkg = parsed.data as SyncPackage;
  if (pkg.formatVersion > SYNC_FORMAT_VERSION) throw new SyncPackageError("Pacchetto creato da una versione più recente dell'app: aggiorna l'app.");
  if (pkg.schemaVersion > SCHEMA_VERSION) throw new SyncPackageError("Pacchetto con uno schema dati più recente: aggiorna l'app.");
  if ((await sha256Hex(canonicalJson(pkg.payload))) !== pkg.checksum) throw new SyncPackageError("Checksum non valido: il file è danneggiato o modificato.");
  // Packages written by older schema versions are upgraded in memory (the checksum covers the original).
  pkg.payload.records.expenses = pkg.payload.records.expenses.map(upgradeExpenseRecord);

  const { reference, records } = pkg.payload;
  const ctx: ReferenceContext = {
    categories: new Map(reference.categories.map((c) => [c.id, c])),
    paymentMethods: new Map(reference.paymentMethods.map((p) => [p.id, p])),
  };
  const problems: string[] = [];
  const check = (entity: string, id: string, issues: string[]) => issues.forEach((i) => problems.push(`${entity} ${id}: ${i}`));
  for (const e of records.expenses) check("Spesa", e.id, guard(() => expenseIssues(e, ctx, { requireActiveCategory: false })));
  for (const i of records.incomes) check("Entrata", i.id, guard(() => incomeIssues(i)));
  for (const b of records.budgets) check("Budget", b.id, guard(() => budgetLineIssues(b, ctx)));
  for (const b of records.incomeBudgets) check("Entrate previste", b.id, guard(() => incomeBudgetIssues(b)));
  for (const a of records.balanceAnchors) check("Saldo", a.id, guard(() => balanceAnchorIssues(a)));
  if (problems.length) throw new SyncPackageError("Il pacchetto contiene dati non validi: nessun dato è stato importato.", problems);
  return pkg;
}

export interface SyncImportReport {
  packageId: string;
  alreadyImported: boolean;
  added: number;
  updated: number;
  unchanged: number;
  /** Local copy has a higher version: kept (the other device should import this device's package). */
  keptLocal: number;
  referenceUpdated: number;
}

const SYNC_RANK: Record<SyncState["status"], number> = { not_queued: 0, pending: 1, exported: 2, failed: 2, blocked: 2, synced: 3 };

function mergeSync(local: SyncState, incoming: SyncState): SyncState {
  // Same version on both sides: keep whichever is further along (e.g. "synced" on the PC).
  return SYNC_RANK[incoming.status] > SYNC_RANK[local.status] ? incoming : local;
}

/** Merges a validated package into the local database, atomically and idempotently. */
export async function applySyncPackage(db: BudgetDatabase, pkg: SyncPackage): Promise<SyncImportReport> {
  const report: SyncImportReport = { packageId: pkg.packageId, alreadyImported: false, added: 0, updated: 0, unchanged: 0, keptLocal: 0, referenceUpdated: 0 };
  const tables = [db.categoryGroups, db.categories, db.paymentMethods, db.expenses, db.incomes, db.budgets, db.incomeBudgets, db.balanceAnchors, db.transfers];
  await db.transaction("rw", tables, async () => {
    const previous = await db.transfers.where("kind").equals("sync-import").toArray();
    report.alreadyImported = previous.some((t) => t.summary.packageId === pkg.packageId);

    const { reference, records } = pkg.payload;
    // Reference data: newer updatedAt wins (ids are deterministic or fixed across devices).
    for (const [table, rows] of [
      [db.categoryGroups, reference.categoryGroups],
      [db.categories, reference.categories],
      [db.paymentMethods, reference.paymentMethods],
    ] as const) {
      for (const row of rows as { id: string; updatedAt: string }[]) {
        const local = (await (table as { get(id: string): Promise<{ updatedAt: string } | undefined> }).get(row.id)) ?? null;
        if (!local || row.updatedAt > local.updatedAt) {
          await (table as unknown as { put(r: unknown): Promise<unknown> }).put(row);
          report.referenceUpdated++;
        }
      }
    }

    // Ledgers: higher version wins; equal version merges sync state only.
    for (const [table, rows] of [
      [db.expenses, records.expenses],
      [db.incomes, records.incomes],
    ] as const) {
      for (const row of rows as (Expense | Income)[]) {
        const local = (await (table as unknown as { get(id: string): Promise<Expense | Income | undefined> }).get(row.id)) ?? null;
        const put = (r: unknown) => (table as unknown as { put(r: unknown): Promise<unknown> }).put(r);
        if (!local) {
          await put(row);
          report.added++;
        } else if (row.version > local.version) {
          await put({ ...row, sync: { ...row.sync, syncedVersion: local.sync.syncedVersion, syncedAt: local.sync.syncedAt, excelRowHint: local.sync.excelRowHint } });
          report.updated++;
        } else if (row.version === local.version) {
          const merged = mergeSync(local.sync, row.sync);
          if (merged !== local.sync) await put({ ...local, sync: merged });
          report.unchanged++;
        } else {
          report.keptLocal++;
        }
      }
    }

    // Planning data: last writer (updatedAt) wins.
    for (const [table, rows] of [
      [db.budgets, records.budgets],
      [db.incomeBudgets, records.incomeBudgets],
      [db.balanceAnchors, records.balanceAnchors],
    ] as const) {
      for (const row of rows as { id: string; updatedAt: string }[]) {
        const local = (await (table as unknown as { get(id: string): Promise<{ updatedAt: string } | undefined> }).get(row.id)) ?? null;
        if (!local) {
          await (table as unknown as { put(r: unknown): Promise<unknown> }).put(row);
          report.added++;
        } else if (row.updatedAt > local.updatedAt) {
          await (table as unknown as { put(r: unknown): Promise<unknown> }).put(row);
          report.updated++;
        } else {
          report.unchanged++;
        }
      }
    }

    await db.transfers.add({
      id: newId(),
      kind: "sync-import",
      createdAt: nowIso(),
      summary: { ...report, sourceDeviceId: pkg.source.deviceId, packageCreatedAt: pkg.createdAt },
    });
  });
  return report;
}
